<script setup lang="ts">
  import dayjs from 'dayjs'

  import type { DateRange } from '../model'

  import { convertDayjsFormatToDateFns, dateRangeToTimestamps, timestampsToDateRange } from '../lib'

  const props = withDefaults(
    defineProps<{
      dateFormat?: string
      dateFormatDisplay?: string
      resettable?: boolean
    }>(),
    {
      dateFormat: 'YYYY/MM/DD',
      dateFormatDisplay: 'DD.MM.YYYY',
      resettable: false,
    },
  )

  const dateRange = defineModel<DateRange>({ required: true })

  const { t } = useI18n()

  const displayFormat = computed(() => convertDayjsFormatToDateFns(props.dateFormatDisplay))

  const pickerValue = computed({
    get: () => dateRangeToTimestamps(dateRange.value, props.dateFormat),
    set: (value: [number, number] | null) => {
      dateRange.value = timestampsToDateRange(value, props.dateFormat)
    },
  })

  const isDateDisabled = (timestamp: number) => {
    const min = dayjs().subtract(2, 'year').startOf('day').valueOf()
    const max = dayjs().endOf('day').valueOf()

    return timestamp < min || timestamp > max
  }
</script>

<template>
  <NDatePicker
    v-model:value="pickerValue"
    type="daterange"
    :format="displayFormat"
    :clearable="resettable"
    :is-date-disabled="isDateDisabled"
    :start-placeholder="t('base_date_start')"
    :end-placeholder="t('base_date_end')"
    class="w-full"
    data-testid="date-range-picker"
  />
</template>
